// src/views/pages/CrmReports.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { useCrmReportsController } from '../../controllers/useCrmReportsController';
import {
  Download,
  Users,
  TrendingUp,
  Kanban,
  Award,
  Filter
} from 'lucide-react';

const CrmReports = () => {
  const {
    loading,
    datePreset,
    setDatePreset,
    handlePresetChange,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    staffPerformance,
    conversionFunnel,
    summaryKPIs,
    handleExportStaffPerformanceCSV,
    handleExportFunnelCSV,
    fetchReportsData
  } = useCrmReportsController();

  return (
    <div className="page-body">
      {/* Top Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 className="page-title">CRM Reports & Performance Analytics</h1>
          <p className="page-subtitle">Track sales team performance, conversion rates, and pipeline stage metrics.</p>
        </div>

        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <Link
            to="/crm"
            className="btn btn-secondary"
            style={{ gap: '6px', fontSize: '13px', textDecoration: 'none' }}
          >
            <Kanban size={15} /> Pipeline Kanban
          </Link>
          <Link
            to="/leads"
            className="btn btn-secondary"
            style={{ gap: '6px', fontSize: '13px', textDecoration: 'none' }}
          >
            <Users size={15} /> Central Leads List
          </Link>
        </div>
      </div>

      {/* Date Filter & Export Bar */}
      <div className="card" style={{ marginBottom: '24px', padding: '16px 20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '14px' }}>
          {/* Quick Presets */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Filter size={14} /> Quick Filter:
            </span>
            {[
              { id: 'today', label: 'Today' },
              { id: 'week', label: 'Last 7 Days' },
              { id: 'month', label: 'This Month' },
              { id: 'last30', label: 'Last 30 Days' },
              { id: 'all', label: 'All Time' }
            ].map(p => (
              <button
                key={p.id}
                type="button"
                onClick={() => handlePresetChange(p.id)}
                className="btn btn-sm"
                style={{
                  fontSize: '11px',
                  padding: '4px 10px',
                  backgroundColor: datePreset === p.id ? 'var(--primary)' : 'var(--bg-secondary)',
                  color: datePreset === p.id ? '#fff' : 'var(--text-secondary)',
                  border: '1px solid var(--border-primary)',
                  borderRadius: 'var(--radius-sm)'
                }}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Custom Date Pickers */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>From:</span>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => { setFromDate(e.target.value); setDatePreset('custom'); }}
                className="filter-select"
                style={{ fontSize: '12px', padding: '4px 8px' }}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>To:</span>
              <input
                type="date"
                value={toDate}
                onChange={(e) => { setToDate(e.target.value); setDatePreset('custom'); }}
                className="filter-select"
                style={{ fontSize: '12px', padding: '4px 8px' }}
              />
            </div>

            <button
              type="button"
              onClick={fetchReportsData}
              className="btn btn-primary btn-sm"
              style={{ fontSize: '12px', padding: '5px 12px' }}
            >
              Apply
            </button>
          </div>
        </div>

        {/* CSV Export Buttons */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', borderTop: '1px solid var(--border-primary)', marginTop: '14px', paddingTop: '12px', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={handleExportStaffPerformanceCSV}
            className="btn btn-secondary btn-sm"
            style={{ gap: '6px', fontSize: '12px' }}
          >
            <Download size={14} /> Export Staff Performance (CSV)
          </button>
          <button
            type="button"
            onClick={handleExportFunnelCSV}
            className="btn btn-secondary btn-sm"
            style={{ gap: '6px', fontSize: '12px' }}
          >
            <Download size={14} /> Export Conversion Funnel (CSV)
          </button>
        </div>
      </div>

      {/* Summary KPI Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '16px',
        marginBottom: '24px'
      }}>
        <div className="card" style={{ padding: '16px' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)' }}>
            Total Leads in Period
          </div>
          <div style={{ fontSize: '24px', fontWeight: 800, color: 'var(--text-primary)', marginTop: '6px' }}>
            {summaryKPIs.totalAssigned}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>
            {summaryKPIs.totalActive} active in pipeline
          </div>
        </div>

        <div className="card" style={{ padding: '16px' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: '#059669' }}>
            Deals Closed (Won)
          </div>
          <div style={{ fontSize: '24px', fontWeight: 800, color: '#059669', marginTop: '6px' }}>
            {summaryKPIs.totalWon}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
            Successful conversions
          </div>
        </div>

        <div className="card" style={{ padding: '16px' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: '#2563eb' }}>
            Site Visits Conducted
          </div>
          <div style={{ fontSize: '24px', fontWeight: 800, color: '#2563eb', marginTop: '6px' }}>
            {summaryKPIs.totalSiteVisits}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
            Physical property inspections
          </div>
        </div>

        <div className="card" style={{ padding: '16px' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: '#dc2626' }}>
            Deals Lost / Inactive
          </div>
          <div style={{ fontSize: '24px', fontWeight: 800, color: '#dc2626', marginTop: '6px' }}>
            {summaryKPIs.totalLost}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
            Not interested or lost
          </div>
        </div>

        <div className="card" style={{ padding: '16px', backgroundColor: 'var(--brand-accent-subtle)', border: '1px solid var(--brand-accent-soft)' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--primary)' }}>
            Overall Conversion Rate
          </div>
          <div style={{ fontSize: '24px', fontWeight: 800, color: 'var(--primary)', marginTop: '6px' }}>
            {summaryKPIs.overallConversionRate}%
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>
            Lead to closed deal ratio
          </div>
        </div>
      </div>

      {/* Staff Performance Report Table */}
      <div className="card" style={{ marginBottom: '24px', padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-primary)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ fontSize: '15px', fontWeight: 700, margin: '0 0 2px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Users size={16} color="var(--primary)" /> Staff Performance & Lead Conversion Report
            </h3>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0 }}>
              Individual breakdown of leads assigned, active progress, and closure rates.
            </p>
          </div>
          <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: 600 }}>
            {staffPerformance.length} Team Members
          </span>
        </div>

        {loading ? (
          <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)' }}>
            Loading staff performance report...
          </div>
        ) : staffPerformance.length === 0 ? (
          <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)' }}>
            No staff records or assigned leads found for this date range.
          </div>
        ) : (
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Staff Member</th>
                <th>Role</th>
                <th style={{ textAlign: 'center' }}>Total Leads</th>
                <th style={{ textAlign: 'center' }}>Active Leads</th>
                <th style={{ textAlign: 'center' }}>Site Visits</th>
                <th style={{ textAlign: 'center' }}>Deals Won</th>
                <th style={{ textAlign: 'center' }}>Deals Lost</th>
                <th style={{ minWidth: '180px' }}>Conversion Rate</th>
              </tr>
            </thead>
            <tbody>
              {staffPerformance.map((staff, idx) => (
                <tr key={staff.staff_id || idx}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        backgroundColor: 'var(--primary)',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: '12px'
                      }}>
                        {(staff.staff_name || 'S').charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '13px', color: 'var(--text-primary)' }}>
                          {staff.staff_name}
                        </div>
                        {staff.deals_won > 0 && (
                          <div style={{ fontSize: '10px', color: '#059669', display: 'flex', alignItems: 'center', gap: '2px', fontWeight: 600 }}>
                            <Award size={10} /> Top Performer
                          </div>
                        )}
                      </div>
                    </div>
                  </td>

                  <td>
                    <span className="badge badge-pending" style={{ fontSize: '11px' }}>
                      {staff.role}
                    </span>
                  </td>

                  <td style={{ textAlign: 'center', fontWeight: 700, fontSize: '13px' }}>
                    {staff.total_assigned}
                  </td>

                  <td style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
                    {staff.active_leads}
                  </td>

                  <td style={{ textAlign: 'center', color: 'var(--brand-accent)', fontWeight: 600 }}>
                    {staff.site_visits}
                  </td>

                  <td style={{ textAlign: 'center', color: '#059669', fontWeight: 700 }}>
                    {staff.deals_won}
                  </td>

                  <td style={{ textAlign: 'center', color: '#64748b' }}>
                    {staff.deals_lost}
                  </td>

                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ flex: 1, height: '8px', backgroundColor: 'var(--border-primary)', borderRadius: '999px', overflow: 'hidden' }}>
                        <div
                          style={{
                            height: '100%',
                            width: `${Math.min(staff.conversion_rate_pct || 0, 100)}%`,
                            backgroundColor: (staff.conversion_rate_pct || 0) >= 30 ? '#059669' : (staff.conversion_rate_pct || 0) >= 10 ? 'var(--primary)' : '#f59e0b',
                            borderRadius: '999px',
                            transition: 'width 0.4s ease'
                          }}
                        />
                      </div>
                      <span style={{ fontSize: '12px', fontWeight: 700, minWidth: '40px', color: 'var(--text-primary)' }}>
                        {staff.conversion_rate_pct || 0}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Conversion Funnel Breakdown */}
      <div className="card">
        <h3 style={{ fontSize: '15px', fontWeight: 700, margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <TrendingUp size={16} color="var(--primary)" /> Pipeline Stage Conversion Funnel
        </h3>
        <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '18px' }}>
          Progression of leads from initial enquiry to closed transaction.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px' }}>
          {conversionFunnel.map((item) => (
            <div
              key={item.stage}
              style={{
                backgroundColor: 'var(--bg-secondary)',
                borderRadius: 'var(--radius-sm)',
                padding: '14px',
                border: '1px solid var(--border-primary)',
                position: 'relative'
              }}
            >
              <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '6px' }}>
                Stage {item.stage_order}: {item.stage}
              </div>
              <div style={{ fontSize: '22px', fontWeight: 800, color: 'var(--text-primary)' }}>
                {item.leads_count}
              </div>
              <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                {item.pct_of_total}% of all leads
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CrmReports;

